/**
 * Register Page
 * Handles new user registration
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import AuthLayout from '../../components/auth/AuthLayout';
import FormInput from '../../components/auth/FormInput';
import Button from '../../components/auth/Button';
import Alert from '../../components/auth/Alert';
import OAuthButton from '../../components/auth/OAuthButton';
import PasswordStrengthIndicator from '../../components/auth/PasswordStrengthIndicator';
import { validateRegisterForm, hasErrors } from '../../utils/validation';
import { FormErrors } from '../../types/auth';
import { computeIsChild, SUPPORTED_COUNTRIES } from '../../utils/childAgeThreshold';
import { getPrivacyRegulation } from '../../utils/privacyRegulation';

import { API_BASE_URL as API_URL } from '../../config';

const RegisterPage: React.FC = () => {
  const { t, i18n } = useTranslation('auth');
  const navigate = useNavigate();
  const { register, isAuthenticated, isLoading, error, clearError } = useAuth();

  // Form state
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [parentName, setParentName] = useState('');
  const [parentConsentAgreed, setParentConsentAgreed] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successVariant, setSuccessVariant] = useState<'adult' | 'child'>('adult');

  // Client-side child detection — the server is authoritative, but mirroring
  // the logic lets us reveal the parent fields inline instead of waiting for
  // a round-trip rejection.
  const isChildComputed = birthDate && countryCode
    ? computeIsChild(birthDate, countryCode)
    : false;

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Clear auth error when component unmounts
  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});
    clearError();

    // Validate form
    const errors = validateRegisterForm({
      email,
      username,
      password,
      confirmPassword,
      birthDate,
      countryCode,
      isChildComputed,
      parentEmail,
      parentName,
      parentConsentAgreed,
    });

    if (hasErrors(errors)) {
      setFormErrors(errors);
      return;
    }

    setSubmitLoading(true);

    try {
      const response = await register({
        email,
        username,
        password,
        birthDate,
        countryCode,
        languagePreference: i18n.language,
        ...(isChildComputed
          ? { parentEmail, parentName, parentConsentAgreed }
          : {}),
      });
      setSuccessVariant(response.requiresParentalConsent ? 'child' : 'adult');
      setShowSuccess(true);
    } catch (err) {
      // Error is handled by AuthContext
    } finally {
      setSubmitLoading(false);
    }
  };

  // Show success message after registration
  if (showSuccess) {
    const isChildSuccess = successVariant === 'child';
    return (
      <AuthLayout
        title={isChildSuccess ? t('register.childSuccessTitle') : t('register.successTitle')}
        subtitle={isChildSuccess ? t('register.childSuccessSubtitle') : t('register.successSubtitle')}
      >
        <div className="text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {isChildSuccess ? t('register.parentCheckEmail') : t('register.checkEmail')}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('register.sentTo')}{' '}
              <strong>{isChildSuccess ? parentEmail : email}</strong>.{' '}
              {isChildSuccess ? t('register.parentClickToConsent') : t('register.clickToVerify')}
            </p>
            {isChildSuccess ? (
              <p className="text-sm text-gray-500">
                {t('register.childAccountGated')}
              </p>
            ) : (
              <p className="text-sm text-gray-500">
                {t('register.didntReceive')}{' '}
                <button
                  onClick={() => {
                    navigate('/login');
                  }}
                  className="text-primary-600 hover:underline"
                >
                  {t('register.resendEmail')}
                </button>
              </p>
            )}
          </div>

          <Link to="/login">
            <Button fullWidth variant="outline">
              {t('register.goToLogin')}
            </Button>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('register.title')}
      subtitle={t('register.subtitle')}
    >
      {error && (
        <Alert type="error" onDismiss={clearError}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <FormInput
          label={t('register.email')}
          type="email"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={formErrors.email}
          placeholder={t('placeholders.email')}
          autoComplete="email"
          disabled={submitLoading}
        />

        <FormInput
          label={t('register.username')}
          type="text"
          name="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          error={formErrors.username}
          placeholder={t('placeholders.username')}
          helperText={t('register.usernameHelperText')}
          autoComplete="username"
          disabled={submitLoading}
        />

        <div className="mb-4">
          <div className="relative">
            <FormInput
              label={t('register.password')}
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={formErrors.password}
              placeholder={t('placeholders.password')}
              autoComplete="new-password"
              disabled={submitLoading}
            />
            <button
              type="button"
              className="absolute right-3 top-[38px] text-gray-400 hover:text-gray-600"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29M3 3l18 18" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
          <PasswordStrengthIndicator password={password} />
        </div>

        <div className="relative">
          <FormInput
            label={t('register.confirmPassword')}
            type={showConfirmPassword ? 'text' : 'password'}
            name="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={formErrors.confirmPassword}
            placeholder={t('placeholders.confirmPassword')}
            autoComplete="new-password"
            disabled={submitLoading}
          />
          <button
            type="button"
            className="absolute right-3 top-[38px] text-gray-400 hover:text-gray-600"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            tabIndex={-1}
          >
            {showConfirmPassword ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29M3 3l18 18" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            )}
          </button>
        </div>

        <FormInput
          label={t('register.birthDate')}
          type="date"
          name="birthDate"
          value={birthDate}
          onChange={(e) => setBirthDate(e.target.value)}
          error={formErrors.birthDate}
          disabled={submitLoading}
          max={new Date().toISOString().split('T')[0]}
        />

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('register.country')}
          </label>
          <select
            name="countryCode"
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            disabled={submitLoading}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              formErrors.countryCode ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">{t('register.countryPlaceholder')}</option>
            {SUPPORTED_COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>
                {t(`countries.${c.nameKey}`)}
              </option>
            ))}
          </select>
          {formErrors.countryCode && (
            <p className="mt-1 text-sm text-red-600">{formErrors.countryCode}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">{t('register.countryHelper')}</p>
        </div>

        {birthDate && countryCode && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-xs text-blue-900">
              {t('register.privacyRegulationNote', {
                regulation: t(`privacyRegulations.${getPrivacyRegulation(countryCode)}`),
              })}
            </p>
          </div>
        )}

        {isChildComputed && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-md">
            <h4 className="text-sm font-semibold text-amber-900 mb-2">
              {t('register.parentSectionTitle')}
            </h4>
            <p className="text-xs text-amber-800 mb-3">
              {t('register.parentSectionIntro')}
            </p>
            <FormInput
              label={t('register.parentName')}
              type="text"
              name="parentName"
              value={parentName}
              onChange={(e) => setParentName(e.target.value)}
              error={formErrors.parentName}
              placeholder={t('placeholders.parentName')}
              disabled={submitLoading}
            />
            <FormInput
              label={t('register.parentEmail')}
              type="email"
              name="parentEmail"
              value={parentEmail}
              onChange={(e) => setParentEmail(e.target.value)}
              error={formErrors.parentEmail}
              placeholder={t('placeholders.parentEmail')}
              helperText={t('register.parentEmailHelper')}
              disabled={submitLoading}
            />
            <label className="flex items-start gap-2 mt-2">
              <input
                type="checkbox"
                checked={parentConsentAgreed}
                onChange={(e) => setParentConsentAgreed(e.target.checked)}
                disabled={submitLoading}
                className="mt-1"
              />
              <span className="text-xs text-amber-900">
                {t('register.parentConsentAgreement')}
              </span>
            </label>
            {formErrors.parentConsentAgreed && (
              <p className="mt-1 text-sm text-red-600">{formErrors.parentConsentAgreed}</p>
            )}
          </div>
        )}

        <Button
          type="submit"
          fullWidth
          isLoading={submitLoading || isLoading}
          disabled={submitLoading || isLoading}
        >
          {t('register.submit')}
        </Button>
      </form>

      {/* OAuth Divider */}
      <div className="my-6 flex items-center">
        <div className="flex-1 border-t border-gray-300" />
        <span className="px-4 text-sm text-gray-500">{t('register.orSignUpWith')}</span>
        <div className="flex-1 border-t border-gray-300" />
      </div>

      {/* OAuth Buttons */}
      <div className="space-y-3">
        <OAuthButton
          provider="google"
          label={t('oauth.continueWithGoogle')}
          onClick={() => {
            window.location.href = `${API_URL}/api/v1/auth/google`;
          }}
          disabled={submitLoading || isLoading}
        />
        <OAuthButton
          provider="github"
          label={t('oauth.continueWithGithub')}
          onClick={() => {
            window.location.href = `${API_URL}/api/v1/auth/github`;
          }}
          disabled={submitLoading || isLoading}
        />
      </div>

      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600">
          {t('register.hasAccount')}{' '}
          <Link
            to="/login"
            className="font-medium text-primary-600 hover:text-primary-700 hover:underline"
          >
            {t('register.signIn')}
          </Link>
        </p>
      </div>

      <div className="mt-4 text-center">
        <p className="text-xs text-gray-500">
          {t('register.agreeTerms')}{' '}
          <a href="/terms" className="text-primary-600 hover:underline">
            {t('register.termsOfService')}
          </a>{' '}
          {t('register.and')}{' '}
          <a href="/privacy" className="text-primary-600 hover:underline">
            {t('register.privacyPolicy')}
          </a>
        </p>
      </div>
    </AuthLayout>
  );
};

export default RegisterPage;
